import { Box, Button, IconButton } from '@youversion/design-system-components';
import PlusStroked from '@youversion/design-system-icons/lib/PlusStroked';
import XStroked from '@youversion/design-system-icons/lib/XStroked';
import ModalFooter from 'components/ui/modal/Footer';
import ModalHeader from 'components/ui/modal/Header';
import PaymentMethods from 'components/views/giving/payment-methods';
import { useSelectorEvents } from 'hooks/useSelectorEvents';
import { useTranslation } from 'next-i18next';
import { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { firebaseLogEvent } from 'utils/firebase';
import { usePaymentMethod } from 'utils/store/paymentMethod';

interface Props {
  header: Element | DocumentFragment | null;
  onAdd: () => void;
  onClose: () => void;
}

export default function PaymentMethodDialog({ onClose, onAdd, header }: Props) {
  const { t } = useTranslation('common');
  const { paymentMethodType } = usePaymentMethod();
  const [editing, setEditing] = useState<boolean>(false);
  const { logSelectorDismissedEvent, logSelectorPresentedEvent } = useSelectorEvents('giving_payment_methods');

  const toggleEdit = () => {
    firebaseLogEvent('GivingButtonTap', { label: 'Edit', screen: 'giving_payment_methods' });
    setEditing(!editing);
  };

  useEffect(() => {
    void firebaseLogEvent('GivingScreenView', { screen: 'giving_payment_methods' });
    logSelectorPresentedEvent(paymentMethodType ?? '');
    return () => void logSelectorDismissedEvent(paymentMethodType ?? '');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <>
      {header
        ? createPortal(
            <ModalHeader
              left={
                <IconButton
                  className='inline-end-2 block-start-2'
                  data-testid='close-modal-button'
                  onClick={() => {
                    firebaseLogEvent('GivingButtonTap', { label: 'close', screen: 'giving_payment_methods' });
                    onClose();
                  }}
                >
                  <XStroked />
                  <label className='sr-only'>{t('giving.cta.close')}</label>
                </IconButton>
              }
              right={
                <Button onClick={toggleEdit} size='md' variant='borderless'>
                  {editing ? t('giving.cta.done') : t('giving.cta.edit')}
                </Button>
              }
              title={t('giving.titles.payment_methods')}
            />,
            header
          )
        : null}
      <Box className='grow overflow-x-auto pli-3 pbs-0.5'>
        <PaymentMethods editing={editing} />
        <Button
          data-testid='add-payment-method'
          icon={PlusStroked}
          iconPosition='start'
          onClick={() => {
            firebaseLogEvent('GivingButtonTap', {
              label: 'Add New Payment Method',
              screen: 'giving_payment_methods',
            });
            onAdd();
          }}
          size='md'
          variant='borderless'
        >
          {t('giving.cta.add_payment_method')}
        </Button>
      </Box>
      {editing ? null : <ModalFooter buttonText={t('giving.cta.done')} onClick={onClose} />}
    </>
  );
}
